import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PROJECT_BATCH_IMAGES } from "@/data/projectBatchImages";

const finishedPhotos = PROJECT_BATCH_IMAGES["/projects/livonia-subfloor"].photos
  .filter(photo => photo.baseName.startsWith("michigan-plank-hickory-fireside-"));

// Symmetrical desktop mosaic: 2x2 dining table centered, mirrored side columns.
const livoniaPlacement: Record<string, { span: string; position?: string }> = {
  "michigan-plank-hickory-fireside-dining-table-livonia-mi": { span: "md:col-start-2 md:col-span-2 md:row-start-1 md:row-span-2" },
  "michigan-plank-hickory-fireside-living-room-sofa-livonia-mi": { span: "md:col-start-1 md:row-start-1" },
  "michigan-plank-hickory-fireside-dining-hutch-livonia-mi": { span: "md:col-start-1 md:row-start-2", position: "object-bottom" },
  "michigan-plank-hickory-fireside-long-boards-sideboard-livonia-mi": { span: "md:col-start-4 md:row-start-1", position: "object-bottom" },
  "michigan-plank-hickory-fireside-grain-closeup-livonia-mi": { span: "md:col-start-4 md:row-start-2" },
};

const LivoniaProjectSection = () => (
  <section className="bg-background py-14 md:py-20 lg:py-28" id="livonia-project">
    <div className="max-w-[1200px] mx-auto px-5 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-8 md:mb-12"
      >
        <p className="text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-2">
          OUR HOME TOWN
        </p>
        <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-foreground leading-[1.1]">
          A Livonia floor, <em className="italic font-normal text-primary">start to finish.</em>
        </h2>
        <p className="mt-3 md:mt-4 text-[13px] md:text-[15px] font-light text-muted-foreground leading-relaxed max-w-xl">
          Leveling came first. With the old mastic primed and encapsulated under self leveling underlayment, the 8 inch engineered Michigan Plank hickory in Fireside went down over a new, sound base, and these photos show that finished floor.
        </p>
      </motion.div>
      <div className="rounded-[28px] overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-[4px] grid-flow-dense auto-rows-[minmax(220px,1fr)]">
          {finishedPhotos.map((photo, i) => (
            <motion.div
              key={photo.baseName}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className={`group relative overflow-hidden block aspect-[4/3] md:aspect-auto min-h-[220px] md:min-h-0 ${livoniaPlacement[photo.baseName]?.span ?? ""}`}
            >
              <img
                src={`/images/projects/livonia-subfloor/${photo.baseName}.webp`}
                alt={photo.alt}
                width={i === 1 || i === 4 ? 1600 : 1066}
                height={i === 1 || i === 4 ? 1066 : 1600}
                loading="lazy"
                className={`absolute inset-0 w-full h-full object-cover ${livoniaPlacement[photo.baseName]?.position ?? ""} grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.05] transition-all duration-[550ms] ease-[cubic-bezier(.25,.46,.45,.94)]`}
              />
            </motion.div>
          ))}
        </div>
      </div>
      <div className="mt-8 md:mt-10">
        <Button asChild className="h-auto bg-foreground text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-primary transition-colors">
          <Link to="/projects/livonia-subfloor">
            See the Livonia project <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </div>
    </div>
  </section>
);

export default LivoniaProjectSection;